import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '@/utils/api';
import type { BlogPost, BlogFormData, BlogFilters } from '@/types';

interface BlogsState {
  posts: BlogPost[];
  currentPost: BlogPost | null;
  filters: BlogFilters;
  isLoading: boolean;
  error: string | null;
  totalCount: number;
}

const initialState: BlogsState = {
  posts: [],
  currentPost: null,
  filters: {},
  isLoading: false,
  error: null,
  totalCount: 0,
};

export const fetchBlogs = createAsyncThunk(
  'blogs/fetchBlogs',
  async (filters: BlogFilters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filters.status?.length) params.set('status', filters.status.join(','));
      if (filters.visibility?.length) params.set('visibility', filters.visibility.join(','));
      if (filters.topicId) params.set('topicId', filters.topicId);
      if (filters.tags?.length) params.set('tags', filters.tags.join(','));
      if (filters.search) params.set('search', filters.search);

      const response = await api.get(`/blogs?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch blogs'
      );
    }
  }
);

export const fetchBlog = createAsyncThunk(
  'blogs/fetchBlog',
  async (blogId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/blogs/${blogId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch blog'
      );
    }
  }
);

export const fetchBlogBySlug = createAsyncThunk(
  'blogs/fetchBlogBySlug',
  async (slug: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/blogs/slug/${slug}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch blog'
      );
    }
  }
);

export const createBlog = createAsyncThunk(
  'blogs/createBlog',
  async (data: BlogFormData, { rejectWithValue }) => {
    try {
      const response = await api.post('/blogs', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to create blog'
      );
    }
  }
);

export const updateBlog = createAsyncThunk(
  'blogs/updateBlog',
  async ({ id, data }: { id: string; data: Partial<BlogFormData> }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/blogs/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update blog'
      );
    }
  }
);

export const publishBlog = createAsyncThunk(
  'blogs/publishBlog',
  async (blogId: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/blogs/${blogId}/publish`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to publish blog'
      );
    }
  }
);

export const unpublishBlog = createAsyncThunk(
  'blogs/unpublishBlog',
  async (blogId: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/blogs/${blogId}/unpublish`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to unpublish blog'
      );
    }
  }
);

export const deleteBlog = createAsyncThunk(
  'blogs/deleteBlog',
  async (blogId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/blogs/${blogId}`);
      return blogId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to delete blog'
      );
    }
  }
);

const blogsSlice = createSlice({
  name: 'blogs',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<BlogFilters>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setCurrentPost: (state, action: PayloadAction<BlogPost | null>) => {
      state.currentPost = action.payload;
    },
    clearCurrentPost: (state) => {
      state.currentPost = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch blogs
      .addCase(fetchBlogs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBlogs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.posts = action.payload.items || action.payload;
        state.totalCount = action.payload.total || action.payload.length;
      })
      .addCase(fetchBlogs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch single blog
      .addCase(fetchBlog.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchBlog.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPost = action.payload;
      })
      .addCase(fetchBlog.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch blog by slug
      .addCase(fetchBlogBySlug.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchBlogBySlug.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPost = action.payload;
      })
      .addCase(fetchBlogBySlug.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create blog
      .addCase(createBlog.fulfilled, (state, action) => {
        state.posts.unshift(action.payload);
        state.currentPost = action.payload;
        state.totalCount += 1;
      })
      // Update blog
      .addCase(updateBlog.fulfilled, (state, action) => {
        const index = state.posts.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.posts[index] = action.payload;
        }
        if (state.currentPost?.id === action.payload.id) {
          state.currentPost = action.payload;
        }
      })
      // Publish blog
      .addCase(publishBlog.fulfilled, (state, action) => {
        const index = state.posts.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.posts[index] = action.payload;
        }
        if (state.currentPost?.id === action.payload.id) {
          state.currentPost = action.payload;
        }
      })
      // Unpublish blog
      .addCase(unpublishBlog.fulfilled, (state, action) => {
        const index = state.posts.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.posts[index] = action.payload;
        }
        if (state.currentPost?.id === action.payload.id) {
          state.currentPost = action.payload;
        }
      })
      // Delete blog
      .addCase(deleteBlog.fulfilled, (state, action) => {
        state.posts = state.posts.filter((p) => p.id !== action.payload);
        state.totalCount -= 1;
        if (state.currentPost?.id === action.payload) {
          state.currentPost = null;
        }
      });
  },
});

export const { setFilters, clearFilters, setCurrentPost, clearCurrentPost } = blogsSlice.actions;
export default blogsSlice.reducer;
